import { cookieAuthRequestInit } from './cookie-auth'
import { ApiError, parseApiError } from './errors'
import type { ApiClient, ApiClientConfig, EmptyRequestOpts, RequestOpts } from './types'

const DEFAULT_TIMEOUT = 90_000
const RETRY_DELAY = 1_000
const RETRYABLE_STATUSES = [502, 503]

type AbortSignalConstructorWithAny = typeof AbortSignal & {
  any?: (signals: AbortSignal[]) => AbortSignal
}

function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError || (err instanceof DOMException && (err.name === 'AbortError' || err.name === 'TimeoutError'))
}

function toNetworkApiError(err: unknown, callerSignal?: AbortSignal): ApiError {
  if (callerSignal?.aborted) {
    return new ApiError(0, 'Request cancelled')
  }
  if (err instanceof DOMException && (err.name === 'AbortError' || err.name === 'TimeoutError')) {
    return new ApiError(0, 'Request timed out')
  }
  const message = err instanceof Error ? err.message : 'Network request failed'
  return new ApiError(0, message)
}

function waitForRetry(delayMs: number, callerSignal?: AbortSignal): Promise<boolean> {
  if (callerSignal?.aborted) return Promise.resolve(false)

  return new Promise<boolean>(resolve => {
    const timer = setTimeout(() => {
      callerSignal?.removeEventListener('abort', onAbort)
      resolve(true)
    }, delayMs)
    const onAbort = () => {
      clearTimeout(timer)
      resolve(false)
    }
    callerSignal?.addEventListener('abort', onAbort, { once: true })
  })
}

function isBodyInitValue(value: unknown): value is BodyInit {
  if (typeof value === 'string') return true
  if (typeof FormData !== 'undefined' && value instanceof FormData) return true
  if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) return true
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true
  if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) return true
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(value)) return true
  if (typeof ReadableStream !== 'undefined' && value instanceof ReadableStream) return true
  return false
}

function toRawResponse<T>(response: Response): T {
  return response as T
}

function buildRequestUrl(baseUrl: string, path: string): string {
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${normalizedBaseUrl}${normalizedPath}`
}

function createRequestSignal(callerSignal: AbortSignal | undefined, timeoutMs: number): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(timeoutMs)
  if (!callerSignal) return timeoutSignal
  if (callerSignal.aborted) return callerSignal

  const combineSignals = (AbortSignal as AbortSignalConstructorWithAny).any

  if (combineSignals) {
    return combineSignals([callerSignal, timeoutSignal])
  }

  const controller = new AbortController()
  const abort = () => controller.abort()
  callerSignal.addEventListener('abort', abort, { once: true })
  timeoutSignal.addEventListener('abort', abort, { once: true })
  return controller.signal
}

/**
 * Build an API path with optional query parameters.
 * Skips undefined, null, and empty-string values.
 */
export function buildQueryPath(
  basePath: string,
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value))
    }
  }
  const qs = query.toString()
  return qs ? `${basePath}?${qs}` : basePath
}

/**
 * Build a path with an org-scope query parameter injected (when an org id is
 * present) alongside optional extra params. Generic org-scoping hook shared by
 * product clients that append the active org to domain requests. Follows the
 * same skip rules as {@link buildQueryPath} (undefined/null/'' are dropped); the
 * org param is placed first and an explicit same-key param overrides it.
 */
export function buildOrgScopedQuery(
  basePath: string,
  orgId: string | number | undefined | null,
  params: Record<string, string | number | boolean | undefined | null> = {},
  orgKey = 'orgId',
): string {
  const hasOrg = orgId !== undefined && orgId !== null && orgId !== ''
  const merged: Record<string, string | number | boolean | undefined | null> = hasOrg
    ? { [orgKey]: orgId, ...params }
    : { ...params }
  return buildQueryPath(basePath, merged)
}

export interface RequestRawOptions {
  /**
   * Abort the request after this many milliseconds. Omit for no timeout (the
   * request is then bounded only by the caller's own `init.signal`, if any).
   * On timeout the underlying fetch rejects with an `AbortError` DOMException,
   * which the caller can map to its own timeout posture.
   */
  timeout?: number
}

/**
 * Low-level transport primitive: performs a single fetch with an optional
 * abort-on-timeout (combined with the caller's `init.signal`) and returns the
 * raw {@link Response} for ANY HTTP status — it never throws on a non-2xx
 * response — while re-throwing the original error verbatim on a network/abort
 * failure.
 *
 * This is the shared transport that thin app clients build on when they must
 * keep their own response/error parsing (status-specific error classes, gate
 * evidence, CSV/text bodies, 204 handling). It deliberately performs NO body
 * serialization, header defaulting, or error mapping — the caller owns those.
 */
export async function requestRaw(
  url: string,
  init: RequestInit = {},
  opts: RequestRawOptions = {},
): Promise<Response> {
  const { timeout } = opts
  if (timeout === undefined) {
    // No timeout configured: pass the caller's init (incl. its own signal) straight through.
    return fetch(url, init)
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout)
  const callerSignal = init.signal ?? undefined
  const onCallerAbort = () => controller.abort()
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort()
    else callerSignal.addEventListener('abort', onCallerAbort, { once: true })
  }

  try {
    return await fetch(url, { ...init, signal: controller.signal })
  } finally {
    clearTimeout(timer)
    if (callerSignal) callerSignal.removeEventListener('abort', onCallerAbort)
  }
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const {
    baseUrl,
    auth,
    timeout = DEFAULT_TIMEOUT,
    retryOnServerError = false,
    onUnauthorized,
    onError,
  } = config

  function reportClientError(error: ApiError, method: string, path: string): void {
    if (!onError) return
    try {
      onError({
        status: error.status,
        message: error.message,
        reference: error.reference,
        method,
        path,
      })
    } catch {
      // Keep transport behavior deterministic even if telemetry callbacks fail.
    }
  }

  async function request<T>(method: string, path: string, body?: unknown, opts?: RequestOpts): Promise<T | void> {
    const url = buildRequestUrl(baseUrl, path)
    const headers: Record<string, string> = { ...opts?.headers }
    const fetchOpts: RequestInit = { method, headers }

    if (opts?.idempotencyKey) {
      headers['Idempotency-Key'] = opts.idempotencyKey
    }
    if (opts?.cache) {
      fetchOpts.cache = opts.cache
    }

    if (body !== undefined) {
      if (isBodyInitValue(body)) {
        fetchOpts.body = body
      } else {
        headers['Content-Type'] = 'application/json'
        fetchOpts.body = JSON.stringify(body)
      }
    }

    if (auth) {
      const authInit = auth.getRequestInit()
      if (authInit.headers) Object.assign(headers, authInit.headers)
      if (authInit.credentials) fetchOpts.credentials = authInit.credentials
    }
    if (opts?.credentials) {
      fetchOpts.credentials = opts.credentials
    }

    const requestTimeout = opts?.timeout ?? timeout
    const responseType = opts?.responseType ?? 'json'

    const canRetry = retryOnServerError && method === 'GET'
    const maxAttempts = canRetry ? 2 : 1

    let lastError: ApiError | undefined

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Create a fresh timeout signal per attempt so retries don't get an already-aborted signal.
      // If the caller provides their own signal, reuse it (caller controls cancellation).
      const attemptOpts: RequestInit = {
        ...fetchOpts,
        signal: createRequestSignal(opts?.signal, requestTimeout),
      }

      let res: Response
      try {
        res = await fetch(url, attemptOpts)
      } catch (err) {
        const networkError = opts?.signal?.aborted || isNetworkError(err)
          ? toNetworkApiError(err, opts?.signal)
          : new ApiError(0, 'Network request failed')

        if (canRetry && attempt < maxAttempts - 1) {
          lastError = networkError
          const shouldRetry = await waitForRetry(RETRY_DELAY * (attempt + 1), opts?.signal)
          if (!shouldRetry) {
            const cancellationError = toNetworkApiError(opts?.signal?.reason, opts?.signal)
            reportClientError(cancellationError, method, path)
            throw cancellationError
          }
          continue
        }

        reportClientError(networkError, method, path)
        throw networkError
      }

      if (res.ok) {
        if (responseType === 'raw') {
          return toRawResponse<T>(res)
        }
        if (responseType === 'empty') {
          return undefined
        }
        if (responseType === 'blob') {
          return await res.blob() as T
        }
        if (responseType === 'text') {
          return await res.text() as T
        }

        const text = await res.text()
        if (!text) {
          const parseError = new ApiError(res.status, 'Empty JSON response from server')
          reportClientError(parseError, method, path)
          throw parseError
        }
        try {
          return JSON.parse(text) as T
        } catch {
          const parseError = new ApiError(res.status, 'Invalid JSON response from server')
          reportClientError(parseError, method, path)
          throw parseError
        }
      }

      const errorText = await res.text().catch(() => '')
      let errorBody: unknown
      try {
        errorBody = errorText ? JSON.parse(errorText) : null
      } catch {
        errorBody = errorText ? { error: errorText } : null
      }

      const apiError = parseApiError(res.status, errorBody)

      if (res.status === 401) onUnauthorized?.()

      if (canRetry && RETRYABLE_STATUSES.includes(res.status) && attempt < maxAttempts - 1) {
        lastError = apiError
        const shouldRetry = await waitForRetry(RETRY_DELAY * (attempt + 1), opts?.signal)
        if (!shouldRetry) {
          const cancellationError = toNetworkApiError(opts?.signal?.reason, opts?.signal)
          reportClientError(cancellationError, method, path)
          throw cancellationError
        }
        continue
      }

      reportClientError(apiError, method, path)
      throw apiError
    }

    throw lastError ?? new ApiError(500, 'Request failed after retries')
  }

  function get(path: string, opts: EmptyRequestOpts): Promise<void>
  function get<T>(path: string, opts?: RequestOpts): Promise<T>
  function get<T>(path: string, opts?: RequestOpts): Promise<T | void> {
    return request<T>('GET', path, undefined, opts)
  }

  function post(path: string, body: unknown, opts: EmptyRequestOpts): Promise<void>
  function post<T>(path: string, body?: unknown, opts?: RequestOpts): Promise<T>
  function post<T>(path: string, body?: unknown, opts?: RequestOpts): Promise<T | void> {
    return request<T>('POST', path, body, opts)
  }

  function put(path: string, body: unknown, opts: EmptyRequestOpts): Promise<void>
  function put<T>(path: string, body?: unknown, opts?: RequestOpts): Promise<T>
  function put<T>(path: string, body?: unknown, opts?: RequestOpts): Promise<T | void> {
    return request<T>('PUT', path, body, opts)
  }

  function patch(path: string, body: unknown, opts: EmptyRequestOpts): Promise<void>
  function patch<T>(path: string, body?: unknown, opts?: RequestOpts): Promise<T>
  function patch<T>(path: string, body?: unknown, opts?: RequestOpts): Promise<T | void> {
    return request<T>('PATCH', path, body, opts)
  }

  function deleteRequest(path: string, opts: EmptyRequestOpts): Promise<void>
  function deleteRequest<T>(path: string, opts?: RequestOpts): Promise<T>
  function deleteRequest<T>(path: string, opts?: RequestOpts): Promise<T | void> {
    return request<T>('DELETE', path, undefined, opts)
  }

  return {
    get,
    post,
    put,
    patch,
    delete: deleteRequest,
  }
}

export interface AuthedClientConfig {
  baseUrl: string
  onUnauthorized?: () => void
  onError?: ApiClientConfig['onError']
  timeout?: number
  retryOnServerError?: boolean
}

export function createAuthedClient(config: AuthedClientConfig): ApiClient {
  return createApiClient({
    baseUrl: config.baseUrl,
    auth: {
      getRequestInit: cookieAuthRequestInit,
    },
    onUnauthorized: config.onUnauthorized,
    onError: config.onError,
    timeout: config.timeout,
    retryOnServerError: config.retryOnServerError ?? true,
  })
}
